import * as React from "react";
import classNames from "classnames";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext } from "react";

import { UsersService } from "@/services";
import { MenuElement } from "@/interfaces";
import { AppContext } from "@/providers/AppContextProvider";
import { URLS } from "@/utils";

import { Sidebar } from "@/components/ui/sidebar";
import { useReviewsState } from "@/containers/Players/Reputation/components/ReviewInbox/hooks/useReviewsState";

interface Props {
  history: any;
  readyToContinue?: boolean;
  menu: MenuElement[];
  logoutRedirectPath?: string;
}

export function SideBarBase({ history, readyToContinue = true, menu, logoutRedirectPath = URLS.login }: Props) {
  const path = history.location?.pathname;
  const queryClient = useQueryClient();
  const context = useContext(AppContext);
  const { unrepliedCount } = useReviewsState();

  const logout = useCallback(async () => {
    try {
      await UsersService.logout();
      context.clearSession();
      queryClient.removeQueries();
      queryClient.clear();
      queryClient.setQueryData(["session"], undefined);
      history.push(logoutRedirectPath);
    } catch (e) {
      alert(e);
    }
  }, [history, logoutRedirectPath, queryClient, context]);

  const handleNavigation = (option: MenuElement) => {
    if (option.externalLink) {
      window.open(option.path, "_blank");
      return;
    }
    if (!readyToContinue) {
      alert("Please save or cancel before leaving this display");
    } else {
      history.push(option.path);
    }
  };

  // Shared button styling to maintain consistency
  const baseButtonClasses =
    "h-12 w-full justify-start rounded-none px-6 text-[15px] font-semibold transition-colors duration-200";
  const activeClasses =
    "bg-white text-slate-600 border-l-[4px] border-blue-400 shadow-sm hover:bg-white hover:text-slate-600";
  const inactiveClasses = "text-slate-400 border-l-[4px] border-transparent hover:bg-slate-100 hover:text-slate-600";

  const renderMenuItems = () => {
    let isItemSelected = false;

    return menu.map((option, index) => {
      const showAsSelected = !isItemSelected && (option.path === path || path?.startsWith(option.path));
      isItemSelected = isItemSelected || showAsSelected;

      return (
        <Sidebar.MenuItem key={`${index}`}>
          <Sidebar.MenuButton
            isActive={showAsSelected}
            onClick={() => handleNavigation(option)}
            className={classNames(baseButtonClasses, showAsSelected ? activeClasses : inactiveClasses)}
          >
            <div className="flex items-center justify-between w-full">
              <span>{option.title}</span>
              {option.title === "Review Inbox" && unrepliedCount > 0 && (
                <span
                  className={classNames(
                    "ml-2 shrink-0 inline-flex items-center justify-center h-5 px-1.5 rounded-full text-[10px] font-extrabold transition-all",
                    showAsSelected
                      ? "bg-indigo-100 text-indigo-700 border border-indigo-200"
                      : "bg-rose-50 text-rose-600 border border-rose-100/70"
                  )}
                >
                  {unrepliedCount}
                </span>
              )}
            </div>
          </Sidebar.MenuButton>
        </Sidebar.MenuItem>
      );
    });
  };

  return (
    <Sidebar className="border-r border-slate-200 bg-slate-50">
      {/* Brand Header */}
      <Sidebar.Header className="mb-4 mt-9.5 px-6">
        <div className="flex items-center gap-3">
          {/* Replace this div with your actual SVG/Image logo */}
          <div className="flex items-center">
            <img
              src="https://cdn.socialcoach.io/socialcoachlogo2.png"
              alt="SocialCoach Logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>
      </Sidebar.Header>

      <Sidebar.Content>
        {/* We remove default padding/gaps from the menu to allow flush, edge-to-edge items */}
        <Sidebar.Menu className="gap-0">{renderMenuItems()}</Sidebar.Menu>
      </Sidebar.Content>

      <Sidebar.Footer className="mb-4">
        <Sidebar.Menu className="gap-0">
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              data-elm-id="sideBar_signOut"
              onClick={logout}
              className={classNames(baseButtonClasses, inactiveClasses)}
            >
              <span>Sign Out</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        </Sidebar.Menu>
      </Sidebar.Footer>
    </Sidebar>
  );
}

export function SideBarMobileTrigger() {
  return (
    <header className="md:hidden flex items-center h-14 border-b border-slate-200 bg-white px-4 shrink-0">
      <Sidebar.Trigger className="text-slate-500" />
      <img
        src="https://cdn.socialcoach.io/socialcoachlogo2.png"
        alt="SocialCoach Logo"
        className="h-8 w-auto object-contain ml-4"
      />
    </header>
  );
}
